// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useState } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';
import { Button, type MantineSize, ButtonGroup, Popover } from '@mantine/core';
import type { GridApi } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { useUserPreferences } from 'hooks/typeCacheHooks/useUserPreferences/useUserPreferences';

import {
  type ExportGridOpts,
  useExportGrid,
  type AgGridExportFormat,
  agGridExportFormats,
  exportGridViaApi,
} from './useExportGrid';

export type ExportButtonProps = {
  size?: MantineSize;
  fullWidth?: boolean;
  compact?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  confirm?: boolean;
};

const ExportButton = ({
  onExport,
  size = 'sm',
  compact = size === 'xs',
  fullWidth,
  disabled,
  loading,
  onClick: parentOnClick,
  confirm = true,
}: ExportButtonProps & {
  onExport: (format: AgGridExportFormat) => Promise<void>;
}) => {
  const [exportFormat, setExportFormat] = useUserPreferences('PREFERRED_EXPORT_FORMAT', 'csv');
  const [showModal, setShowModal] = useState(false);
  const [formatPopoverOpened, setFormatPopoverOpened] = useState(false);

  return (
    <>
      <Button.Group>
        <Button
          onClick={() => {
            parentOnClick?.();
            if (confirm) {
              setShowModal(true);
            } else {
              void onExport(exportFormat);
            }
          }}
          size={compact ? `compact-${size}` : size}
          variant="light"
          leftSection={<EvolveIcon icon="Download" size={size} />}
          disabled={disabled}
          loading={loading}
          flex={fullWidth ? 1 : undefined}
        >
          {compact ? 'Export' : 'Download'} .{exportFormat}
        </Button>

        <Popover position="bottom-end" withArrow opened={formatPopoverOpened} onChange={setFormatPopoverOpened}>
          <Popover.Target>
            <Button
              variant="light"
              size={compact ? `compact-${size}` : size}
              onClick={() => setFormatPopoverOpened((o) => !o)}
              disabled={disabled}
            >
              <EvolveIcon icon="ArrowDown" size="xs" />
            </Button>
          </Popover.Target>
          <Popover.Dropdown>
            <ButtonGroup>
              {agGridExportFormats.map((f) => (
                <Button
                  key={f}
                  variant={f === exportFormat ? 'outline' : 'light'}
                  onClick={() => {
                    setExportFormat(f);
                    setFormatPopoverOpened(false);
                  }}
                >
                  .{f}
                </Button>
              ))}
            </ButtonGroup>
          </Popover.Dropdown>
        </Popover>
      </Button.Group>

      <WrappedModal
        title="Export grid"
        confirmationText="Export"
        opened={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={() => onExport(exportFormat)}
      >
        Are you sure you want to export this data?
      </WrappedModal>
    </>
  );
};

export const ExportGridButton = <T,>({
  gridApi,
  fileName,
  ...props
}: {
  gridApi: GridApi<T> | null;
  fileName: string;
} & ExportButtonProps) => (
  <ExportButton
    {...props}
    onExport={async (format) => {
      if (gridApi) {
        exportGridViaApi(gridApi, {
          fileName,
          format,
        });
      }
    }}
  />
);

export const ExportPaginatedGridButton = <T,>({
  disabled,
  getGridApi,
  gridRef,
  ...props
}: StrongOmit<ExportGridOpts<T>, 'gridApi' | 'format'> &
  (
    | {
        gridRef: React.RefObject<AgGridReact<T> | null>;
        getGridApi?: never;
      }
    | {
        getGridApi: () => GridApi | null | undefined;
        gridRef?: never;
      }
  ) &
  ExportButtonProps) => {
  const { exportGrid, exportRunning } = useExportGrid();
  const [exporting, setExporting] = useState(false);
  return (
    <ExportButton
      {...props}
      disabled={disabled || (exportRunning && !exporting)}
      loading={exporting}
      onExport={async (format) => {
        setExporting(true);
        try {
          const gridApi = getGridApi?.() ?? gridRef?.current?.api;
          if (gridApi) await exportGrid({ ...props, format, gridApi });
        } finally {
          setExporting(false);
        }
      }}
    />
  );
};
